import { LogOutIcon } from 'lucide-react'
import { useNavigate } from 'react-router'

import { paths } from '@/app/navigation'
import { UserAvatar } from '@/components/common/user-avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { ACCOUNT_TYPE_LABEL, roleLabels, useCurrentUser } from '@/modules/accounts'
import { signOut } from '@/modules/auth'
import { useAppDispatch } from '@/store/hooks'

/**
 * Signed-in user's avatar; opens account actions.
 * `rail`: large avatar in the desktop sidebar, menu opens to the right.
 * `header`: compact avatar in the mobile app bar, menu opens downwards.
 */
export function UserMenu({ variant = 'rail' }: { variant?: 'rail' | 'header' }) {
  const navigate = useNavigate()
  const { data: user } = useCurrentUser()
  const dispatch = useAppDispatch()

  const isHeader = variant === 'header'
  const avatarClassName = isHeader ? 'size-9 text-xs' : 'size-10 text-sm'

  if (!user) return <Skeleton className={`${avatarClassName} rounded-full`} />

  function logOut() {
    // Ends this tab's session only; the other tab stays signed in.
    dispatch(signOut())
    navigate(paths.login, { replace: true })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Account menu for ${user.fullName}`}
        className="rounded-full outline-none ring-offset-2 ring-offset-background transition-shadow hover:ring-2 hover:ring-primary/30 focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:ring-2 data-[state=open]:ring-primary/40"
      >
        <UserAvatar name={user.fullName} src={user.account.logoUrl} className={avatarClassName} />
      </DropdownMenuTrigger>

      <DropdownMenuContent side={isHeader ? 'bottom' : 'right'} align="end" sideOffset={isHeader ? 8 : 12} className="w-64">
        <DropdownMenuLabel className="flex items-center gap-3 py-2 font-normal">
          <UserAvatar name={user.fullName} src={user.account.logoUrl} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-text-primary">{user.fullName}</span>
            <span className="block truncate text-xs text-text-tertiary">{user.email}</span>
            <span className="block truncate text-xs text-text-tertiary">
              {user.account.accountName} · {ACCOUNT_TYPE_LABEL[user.account.accountType]} {roleLabels(user)}
            </span>
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={logOut}>
          <LogOutIcon />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
